import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { RefreshCw } from 'lucide-react';
interface SlideTimelineProps {
  onContinue: () => void;
}
export function SlideTimeline({
  onContinue
}: SlideTimelineProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const steps = [{
    title: 'Send existing assets',
    desc: 'No new content creation required'
  }, {
    title: 'Campaigns created',
    desc: 'Ready in days, not months'
  }, {
    title: 'Deployed across markets',
    desc: 'Localized and distributed'
  }, {
    title: 'Behavioral data captured',
    desc: 'Real-time engagement tracking'
  }, {
    title: 'Next campaign optimized',
    desc: 'Continuous learning loop'
  }];
  useEffect(() => {
    if (activeStep < steps.length) {
      const timer = setTimeout(() => {
        setActiveStep(prev => prev + 1);
      }, 1200);
      return () => clearTimeout(timer);
    } else {
      const doneTimer = setTimeout(() => setAllDone(true), 600);
      return () => clearTimeout(doneTimer);
    }
  }, [activeStep, steps.length]);
  return <div className="h-full w-full flex flex-col px-12 py-16 md:px-24 md:py-20 max-w-7xl mx-auto" data-id="element-300">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-white mb-16 text-center" data-id="element-301">
        Snakker runs this for you
      </motion.h2>

      <div className="flex-1 flex flex-col items-center justify-center" data-id="element-302">
        {/* Timeline Container */}
        <div className="w-full max-w-5xl relative" data-id="element-303">
          {/* Connecting Line */}
          <div className="absolute top-6 left-0 w-full h-[2px] bg-navy-600" data-id="element-304">
            <motion.div className="h-full bg-teal-400 shadow-[0_0_8px_rgba(0,212,170,0.5)]" initial={{
            width: '0%'
          }} animate={{
            width: `${Math.min(activeStep, steps.length - 1) / (steps.length - 1) * 100}%`
          }} transition={{
            duration: 0.5
          }} data-id="element-305" />
          </div>

          <div className="flex justify-between relative z-10" data-id="element-306">
            {steps.map((step, idx) => {
            const isActive = idx <= activeStep;
            return <div key={idx} className="flex flex-col items-center w-48 text-center gap-5" data-id="element-307">
                  <motion.div initial={{
                scale: 0.8,
                opacity: 0.3
              }} animate={{
                scale: isActive ? 1 : 0.8,
                opacity: isActive ? 1 : 0.4,
                backgroundColor: isActive ? '#00D4AA' : '#1E2D4A',
                borderColor: isActive ? '#00D4AA' : '#2A3F5F'
              }} className="w-12 h-12 rounded-full border-4 flex items-center justify-center text-navy-900 font-bold shadow-lg" data-id="element-308">
                    {idx + 1}
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: isActive ? 1 : 0.3,
                y: isActive ? 0 : 10
              }} transition={{
                delay: 0.2
              }} data-id="element-309">
                    <h4 className={`font-medium mb-1 ${isActive ? 'text-white' : 'text-white/40'}`} data-id="element-310">
                      {step.title}
                    </h4>
                    <p className={`text-sm ${isActive ? 'text-white/60' : 'text-white/30'}`} data-id="element-311">
                      {step.desc}
                    </p>
                  </motion.div>
                </div>;
          })}
          </div>
        </div>

        {/* Loop indicator + Button grouped together */}
        <div className="mt-16 flex flex-col items-center gap-8" data-id="element-312">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: allDone ? 1 : 0
        }} transition={{
          duration: 0.8
        }} className="flex flex-col items-center text-teal-400/70" data-id="element-313">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'linear'
          }} data-id="element-314">
              <RefreshCw className="w-7 h-7 mb-2" data-id="element-315" />
            </motion.div>
            <span className="text-sm uppercase tracking-widest font-medium" data-id="element-316">
              Continuous Loop
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: allDone ? 1 : 0,
          y: allDone ? 0 : 10
        }} transition={{
          duration: 0.5
        }} data-id="element-317">
            <Button onClick={onContinue} className={allDone ? 'pointer-events-auto' : 'pointer-events-none'} data-id="element-318">
              Start small
            </Button>
          </motion.div>
        </div>
      </div>
    </div>;
}